// 設定画面
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast, fmtRelative } from '../ui.js';
import { fetchBooks, fetchAllNotesParallel } from '../api.js';
import { upsertBooks, getAllBooks, clearBooks, ls } from '../cache.js';
import { drive, uploadBackup } from '../drive.js';

export async function showSettingsScreen() {
  const books = await getAllBooks();
  const lastSync = ls.getLastSync();
  const driveClientId = drive.getClientId();
  const driveLast = drive.getLastBackup();
  const driveAuto = drive.isAutoBackup();

  const html = `
    ${screenHead('設定')}
    <p class="section-label">notion synchronization</p>
    <div class="setting-item">
      <div class="setting-item__body">
        <p class="setting-item__title">読書記録 DB を同期</p>
        <p class="setting-item__desc">現在 ${books.length} 冊保存 · ${escapeHtml(fmtRelative(lastSync))}</p>
      </div>
      <button class="btn btn--ink btn--small" id="btn-sync" type="button">
        <svg class="icon"><use href="#i-refresh"/></svg>
        <span>今すぐ同期</span>
      </button>
    </div>
    <div class="setting-item">
      <div class="setting-item__body">
        <p class="setting-item__title">ローカルキャッシュをクリア</p>
        <p class="setting-item__desc">書籍データと AI 応答キャッシュを削除。次回起動時に再同期が必要</p>
      </div>
      <button class="btn btn--small" id="btn-clear" type="button">削除</button>
    </div>

    <p class="section-label" style="margin-top:32px;">google drive backup</p>
    <div class="setting-item" style="flex-direction:column;align-items:stretch;gap:10px;">
      <div class="setting-item__body" style="width:100%;">
        <p class="setting-item__title">OAuth クライアント ID</p>
        <p class="setting-item__desc">Google Cloud Console で取得した OAuth クライアント ID を貼り付けてください。<a href="#" id="drive-help" style="color:var(--ink);text-decoration:underline;">設定方法</a></p>
      </div>
      <input class="form-input" id="drive-client-id" type="text" placeholder="例: 1234567890-xxxx.apps.googleusercontent.com" value="${escapeHtml(driveClientId)}" />
      <button class="btn btn--small" id="btn-drive-save" type="button">クライアント ID を保存</button>
    </div>
    <div class="setting-item">
      <div class="setting-item__body">
        <p class="setting-item__title">今すぐバックアップ</p>
        <p class="setting-item__desc">最終バックアップ: ${driveLast ? escapeHtml(new Date(driveLast).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })) : 'まだ実行していません'}</p>
      </div>
      <button class="btn btn--ink btn--small" id="btn-drive-backup" type="button" ${driveClientId ? '' : 'disabled'}>
        <svg class="icon"><use href="#i-download"/></svg>
        <span>実行</span>
      </button>
    </div>
    <div class="setting-item">
      <div class="setting-item__body">
        <p class="setting-item__title">月 1 回 自動バックアップ</p>
        <p class="setting-item__desc">起動時に最終バックアップから 30 日以上経過していたら自動実行</p>
      </div>
      <label class="toggle">
        <input type="checkbox" id="drive-auto" ${driveAuto ? 'checked' : ''} ${driveClientId ? '' : 'disabled'} />
        <span class="toggle__track"><span class="toggle__thumb"></span></span>
      </label>
    </div>

    <p class="section-label" style="margin-top:32px;">about</p>
    <div class="setting-item">
      <div class="setting-item__body">
        <p class="setting-item__title">lectio</p>
        <p class="setting-item__desc">読むこと、選び抜くこと。Notion × Claude Opus で読書を多角的に編む PWA。</p>
      </div>
    </div>
    <div class="setting-item">
      <div class="setting-item__body">
        <p class="setting-item__title">使用モデル</p>
        <p class="setting-item__desc">claude-opus-5-5 / claude-sonnet-5-5</p>
      </div>
    </div>
  `;
  const screen = showScreen('settings', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-sync').addEventListener('click', async () => {
    await syncNow();
  });
  screen.querySelector('#btn-clear').addEventListener('click', async () => {
    if (!confirm('ローカルの書籍データと AI 応答キャッシュを削除します。よろしいですか？')) return;
    await clearBooks();
    toast('ローカルキャッシュを削除しました');
    showSettingsScreen();
  });

  // Drive 関連
  screen.querySelector('#btn-drive-save').addEventListener('click', () => {
    const v = screen.querySelector('#drive-client-id').value.trim();
    drive.setClientId(v);
    toast(v ? 'クライアント ID を保存しました' : 'クライアント ID をクリアしました');
    showSettingsScreen();
  });
  screen.querySelector('#btn-drive-backup').addEventListener('click', async () => {
    if (!drive.getClientId()) {
      toast('クライアント ID を先に設定してください');
      return;
    }
    showLoading('Drive と接続中…');
    try {
      const result = await uploadBackup((msg) => {
        const ov = document.getElementById('overlay-msg');
        if (ov) ov.textContent = msg;
      });
      toast(`バックアップ完了: ${result.name}`);
      showSettingsScreen();
    } catch (e) {
      console.error(e);
      toast(`バックアップ失敗: ${e.message || ''}`.slice(0, 80));
    } finally {
      hideLoading();
    }
  });
  screen.querySelector('#drive-auto').addEventListener('change', (e) => {
    drive.setAutoBackup(e.target.checked);
    toast(e.target.checked ? '自動バックアップを ON にしました' : '自動バックアップを OFF にしました');
  });
  screen.querySelector('#drive-help').addEventListener('click', (e) => {
    e.preventDefault();
    showDriveSetupHelp();
  });
}

function showDriveSetupHelp() {
  const html = `
    ${screenHead('Drive 連携の設定手順')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      Google Drive に lectio のバックアップを保存するため、OAuth クライアント ID を取得します（10 分程度）。
    </p>
    <ol class="drive-help">
      <li><strong>Google Cloud Console を開く</strong><br/>
        <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">console.cloud.google.com</a> にアクセスし、新規プロジェクトを作成（または既存のものを選択）。プロジェクト名は「lectio backup」など。
      </li>
      <li><strong>Drive API を有効化</strong><br/>
        左メニュー → 「API とサービス」 → 「ライブラリ」 → 検索欄に「Google Drive API」 → 「有効にする」をクリック。
      </li>
      <li><strong>OAuth 同意画面を設定</strong><br/>
        「OAuth 同意画面」 → ユーザータイプは <b>外部</b> を選択 → アプリ名「lectio」、サポートメール（あなたのメール）を入力 → スコープは追加せずそのまま次へ → テストユーザーに <b>あなたの Google アカウント</b>を追加。
      </li>
      <li><strong>クライアント ID を作成</strong><br/>
        「認証情報」 → 「認証情報を作成」 → 「OAuth クライアント ID」 → アプリケーションタイプ「<b>ウェブアプリケーション</b>」。<br/>
        <b>承認済みの JavaScript 生成元</b>に次の 2 つを登録:
        <ul style="margin:6px 0 6px 16px;font-family:var(--font-mono);font-size:11.5px;line-height:1.8;color:var(--ink);">
          <li>https://lectio-czc.pages.dev</li>
          <li>http://localhost:5182</li>
        </ul>
        「リダイレクト URI」は<b>空のまま</b>で OK。
      </li>
      <li><strong>クライアント ID をコピー</strong><br/>
        作成完了画面に表示される <code>xxx.apps.googleusercontent.com</code> をコピーし、設定画面の入力欄に貼り付けて保存。
      </li>
      <li><strong>テスト中の警告について</strong><br/>
        初回接続時に「このアプリは確認されていません」と表示されますが、<b>あなた自身がテストユーザー</b>なので「詳細」→「（アプリ名）に移動（安全ではないページ）」で進めば問題ありません。
      </li>
    </ol>
    <p style="font-size:11px;color:var(--mute);line-height:1.6;margin-top:14px;">
      スコープは <code>drive.file</code> （本アプリが作成したファイルのみアクセス可能）に限定されており、Drive 内の他のファイルは見えません。
    </p>
    <button class="btn btn--block btn--ghost" id="drive-help-back" type="button" style="margin-top:20px;">
      <svg class="icon"><use href="#i-back"/></svg>
      <span>設定に戻る</span>
    </button>
  `;
  const screen = showScreen('drive-help', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => showSettingsScreen());
  screen.querySelector('#drive-help-back').addEventListener('click', () => showSettingsScreen());
}

// Notion 側に存在せず端末内でのみ管理されるフィールド。
// 同期の put（レコード全置換）で消えると、書影の再生成 = 再課金になるため必ず引き継ぐ。
const LOCAL_ONLY_FIELDS = [
  'hasIllustration', // 静的書影 PNG (/illustrations/<id>.png) の存在フラグ
  'illustrationDataUrl', // アプリ内 AI 生成書影（data URL）
  'illustrationBlob', // 旧形式書影の後方互換
  'coverUrl', // NDL / openBD の実書影 URL
  'acquisition', // 読書の儀式: この本との出会い
  'preReadingQuestion', // 読書の儀式: 最初の問い
  'closingNote', // 読書の儀式: 締めの言葉
];

function mergeLocalFields(fresh, prev) {
  if (!prev) return fresh;
  const merged = { ...fresh };
  for (const key of LOCAL_ONLY_FIELDS) {
    if (merged[key] === undefined && prev[key] !== undefined) merged[key] = prev[key];
  }
  return merged;
}

export async function syncNow() {
  showLoading('Notion から書籍メタ情報を同期中…');
  try {
    const { books, syncedAt } = await fetchBooks();
    showLoading('各書籍のメモを取得中…');
    await fetchAllNotesParallel(books, (done, total) => {
      const ov = document.getElementById('overlay-msg');
      if (ov) ov.textContent = `メモを取得中… (${done}/${total})`;
    });
    const prevById = new Map((await getAllBooks()).map((b) => [b.id, b]));
    await upsertBooks(books.map((b) => mergeLocalFields(b, prevById.get(b.id))));
    ls.setLastSync(syncedAt);
    toast(`${books.length} 冊を同期しました`);
  } catch (e) {
    console.error(e);
    toast('同期に失敗しました');
  } finally {
    hideLoading();
  }
}
